import { createSlice } from '@reduxjs/toolkit';
import { TRootState } from '../../store';
import { TUserActivity, TUserDevice, TUserInfo } from '../../types/types';

export const selectIsVerified = (state: TRootState) => state.user.isVerified;

export const selectShortName = (state: TRootState) => state.user.shortName;

export const selectFullName = (state: TRootState) => state.user.fullName;

export const selectUserInfo = (state: TRootState) => state.user.userInfo;

export const selectUserEmail = (state: TRootState) => state.user.email;
export const selectUserPhone = (state: TRootState) => state.user.phone;

export const selectUserVerificationInfo = (state: TRootState) =>
	state.user.verificationInfo;

export const selectSumSubStatus = (state: TRootState) => state.user.statusSumSubId;

export const selectUserActivity = (state: TRootState) => state.user.activities;

export const selectUserActivityNextFrameId = (state: TRootState) =>
	state.user.nextFrameId;

export const selectUserDevices = (state: TRootState) => state.user.devices;

export const selectCurrentUserDevice = (state: TRootState) =>
	state.user.currentUserDevice;

export const selectSelectedUserDevice = (state: TRootState) =>
	state.user.selectedUserDevice;

export const selectUserActivityIsListEnd = (state: TRootState) =>
	state.user.isActivitiesListEnd;

interface IRootInitialState {
	isVerified: boolean;

	userInfo: TUserInfo[];
	shortName: string;
	fullName: string;
	verificationInfo: string;
	statusSumSubId: string;
	email: string;
	phone: string;
	activities: TUserActivity[];
	nextFrameId: string;
	devices: TUserDevice[];
	currentUserDevice: TUserDevice | undefined;
	selectedUserDevice: TUserDevice | undefined;
	isActivitiesListEnd: boolean;
}

const initialState: IRootInitialState = {
	isVerified:
		JSON.parse(
			localStorage.getItem(localStorage.getItem('userId') as string) as string
		)?.isVerified != undefined
			? Boolean(
				JSON.parse(
					localStorage.getItem(
						localStorage.getItem('userId') as string
					) as string
				)?.isVerified
			)
			: false,
	userInfo: [],
	verificationInfo: '',
	statusSumSubId: '',
	shortName: '',
	fullName: '',
	email: '',
	phone: '',
	activities: [],
	nextFrameId: '',
	devices: [],
	isActivitiesListEnd: false,
	currentUserDevice: undefined,
	selectedUserDevice: undefined,
};

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		setVerificationInfo(state, action) {
			state.verificationInfo = action.payload?.stepId;
			state.statusSumSubId = action.payload?.statusSumSubId;
		},
		setUserInfo(state, action) {
			state.userInfo = action.payload;
			const firstName = action.payload?.find(
				(param: TUserInfo) => param.paramId === 'firstName'
			)?.paramValue;
			const lastName = action.payload?.find(
				(param: TUserInfo) => param.paramId === 'lastName'
			)?.paramValue;
			state.email = action.payload?.find(
				(param: TUserInfo) => param.paramId === 'email'
			)?.paramValue;
			state.phone = action.payload?.find(
				(param: TUserInfo) => param.paramId === 'phone'
			)?.paramValue;

			state.fullName = firstName + ' ' + lastName;
			state.shortName = firstName?.substring(0, 1) + lastName?.substring(0, 1);
		},
		setIsVerified(state, action) {
			const accSettings = localStorage.getItem(
				localStorage.getItem('userId') as string
			);
			if (accSettings) {
				const parsedAccSettings = JSON.parse(accSettings);
				Object.assign(parsedAccSettings, {
					isVerified: action.payload,
				});
				localStorage.setItem(
					localStorage.getItem('userId') as string,
					JSON.stringify(parsedAccSettings)
				);
			} else {
				localStorage.setItem(
					localStorage.getItem('userId') as string,
					JSON.stringify({
						isVerified: action.payload,
					})
				);
			}
			state.isVerified = action.payload;
		},
		setUserActivities(state, action) {
			state.activities.push(...action.payload.activities);
			state.nextFrameId = action.payload.nextFrameId
				? action.payload.nextFrameId
				: '';
			state.isActivitiesListEnd = action.payload.nextFrameId === undefined;
		},
		setUserActivitiesInit(state) {
			state.activities = [];
			state.nextFrameId = '';
		},
		setUserDevices(state, action) {
			state.devices = action.payload?.devices;
			state.currentUserDevice = action.payload?.devices?.find(
				//eslint-disable-next-line
				(device: any) => device?.deviceId === localStorage.getItem('deviceId')
			);
		},
		setSelectedUserDevice(state, action) {
			state.selectedUserDevice = action.payload;
		},
		updateUserDevices(state, action) {
			state.devices = state.devices.filter(
				(device) => device?.deviceId != action.payload
			);
		},
		setUserDevicesInit(state) {
			state.devices = [];
			state.currentUserDevice = undefined;
			state.selectedUserDevice = undefined;
		},
	},
});

export const {
	setIsVerified,
	setUserInfo,
	setVerificationInfo,
	setUserActivities,
	setUserActivitiesInit,
	setUserDevices,
	setUserDevicesInit,
	setSelectedUserDevice,
	updateUserDevices
} = userSlice.actions;
